import { ApolloError, gql } from 'apollo-boost';
import CenterLoader from 'core/components/common/CenterLoader';
import { showSnackbar } from 'core/components/snackbar';
import { button, buttonBlack, field, fieldInput, fieldInputInvalid } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { getErrors } from 'core/utils/apollo';
import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from 'site/components/auth/AuthProvider';
import { AccountSubscriptionFragment } from 'site/components/auth/hooks/useGetMyAccount';
import { GetMyAccountQuery, useUpdateMyAccountMutation } from 'site/graphql';
import Validator from 'validatorjs';
import { phoneRegex } from '../../const';
import AccountProfileInput from './AccountProfileInput';
import SubscribeVkButton from './SubscribeVkButton';

export type IProfileFields = keyof Pick<
    GetMyAccountQuery['myAccount'],
    'email' | 'firstName' | 'lastName' | 'phones' | 'middleName' | 'vkLink' | 'facebookLink' | 'telegramUserName'
>;

const validationRules: { [field in IProfileFields]?: string } = {
    firstName: 'required',
    lastName: 'required',
    vkLink: 'url',
    facebookLink: 'url',
    phones: `regex:${phoneRegex}`,
};

gql`
    mutation UpdateMyAccount($account: AccountPublicUpdateInput!) {
        updateAccount(account: $account) {
            id
            email
            firstName
            lastName
            middleName
            email
            phones
            vkLink
            facebookLink
            telegramUserName
            accountSubscription {
                ...AccountSubscriptionFragment
            }
        }
    }
    ${AccountSubscriptionFragment}
`;

const AccountProfile: React.FC<WithStyles<typeof styles>> = ({ classes }) => {
    const { myAccount } = useAuth();
    const [account, setAccount] = useState(myAccount);
    const [showErrors, setShowErrors] = useState(false);
    const [errors, setErrors] = useState(null);

    const [updateAccount, { loading }] = useUpdateMyAccountMutation({
        onCompleted: () => showSnackbar({ snackbarMessage: 'Изменения сохранены' }),
        onError: (error: ApolloError) => showSnackbar({ snackbarErrors: getErrors(error) }),
    });

    const handleChange = useCallback(
        (fieldName: IProfileFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setAccount(acc => ({ ...acc, [fieldName]: value }));
            setShowErrors(false);
        },
        [],
    );

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const validation = new Validator(account, validationRules);

            if (validation.passes()) {
                updateAccount({
                    variables: {
                        account: {
                            firstName: account.firstName,
                            lastName: account.lastName,
                            middleName: account.middleName,
                            phones: account.phones,
                            telegramUserName: account.telegramUserName,
                            vkLink: account.vkLink,
                            facebookLink: account.facebookLink,
                        },
                    },
                });
            } else {
                setShowErrors(true);
                setErrors(validation.errors);
            }
        },
        [account, updateAccount],
    );

    const inputCommonProps = useMemo(
        () => ({
            account,
            classes,
            onChange: handleChange,
            showErrors,
            errors,
        }),
        [account, classes, handleChange, showErrors, errors],
    );

    return (
        <div className={classes.root}>
            {loading && <CenterLoader absolute />}
            <form onSubmit={handleSubmit}>
                <div className={classes.container}>
                    <div className={classes.row}>
                        <AccountProfileInput
                            {...inputCommonProps}
                            fieldName="lastName"
                            label="Фамилия"
                            autoComplete="family-name"
                        />
                        <AccountProfileInput
                            {...inputCommonProps}
                            label="Имя"
                            fieldName="firstName"
                            autoComplete="given-name"
                        />
                        <AccountProfileInput
                            {...inputCommonProps}
                            label="Отчество"
                            fieldName="middleName"
                            autoComplete="additional-name"
                        />
                        <AccountProfileInput
                            {...inputCommonProps}
                            label="Email"
                            fieldName="email"
                            autoComplete="email"
                            disabled
                        />
                        <AccountProfileInput
                            {...inputCommonProps}
                            label="Телефон"
                            fieldName="phones"
                            autoComplete="tel"
                        />
                        <AccountProfileInput
                            {...inputCommonProps}
                            label="Telegram username без @"
                            fieldName="telegramUserName"
                        />
                    </div>
                    <div className={classes.row}>
                        <AccountProfileInput {...inputCommonProps} label="Ссылка на профиль VK" fieldName="vkLink" />
                        <SubscribeVkButton />
                        {/* <AccountProfileInput
                            {...inputCommonProps}
                            label="Ссылка на профиль Facebook"
                            fieldName="facebookLink"
                        /> */}
                    </div>
                </div>
                <button className={classes.button} type="submit">
                    Сохранить
                </button>
            </form>
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        root: {
            position: 'relative',
        },
        container: {
            display: 'flex',

            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
            },
        },
        row: {
            flexGrow: 1,
        },
        button: {
            ...button(theme),
            ...buttonBlack(theme),
            alignSelf: 'flex-start',
            marginTop: 1.5 + 'rem',

            [theme.breakpoints.down('sm')]: {
                width: 100 + '%',
                marginBottom: 1.5 + 'rem',
            },
        },
        field: {
            ...field,
            marginBottom: 0.5 + 'rem',
        },
        labelphoneInput: {
            ...field,
        },
        field__input: {
            ...fieldInput,
            maxWidth: 350,
        },
        field__inputInvalid: {
            ...fieldInputInvalid,
        },
    });

export default injectSheet(styles, 'AccountProfile')(AccountProfile);
